import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Copy, Loading, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import CompetencyPicker from 'components/admin/forms/competencyPicker';
import WidgetBase, { competencySet, groupSet, groupTooLong, widgetLoad, widgetLoadRecommendations } from '../_base';
import MetricRecommendations from '../components/metricRecommendations';

export const CompetencyOverTime = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [recLoading, setRecLoading] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    const defaults = { show_For: true, period: 'M' };

    const st = (path, ...rest) => SystemText(`Dashboard/Widgets/CompetencyOverTime/${path}`, ...rest);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;

        if (prop === 'type1' && value) temp.metric1 = null;
        if (prop === 'type1' && !value) temp.competency1 = null;

        setValues(temp);
    };

    const height = typeof defaultProps.height === 'function' ? defaultProps.height(item) : 110 * item.h - 100;

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = st('Fields/Group/Error');
            validForm = false;
        }

        if (groupTooLong(form.group1)) {
            errors.group1 = st('Fields/Group/Error/Length');
            validForm = false;
        }

        if (!competencySet(form.competency1)) {
            errors.competency1 = st('Fields/Competency/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart data={[data]} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        if (recommendationData?.improvement) {
            const slug =
                recommendationData?.improvement > 0
                    ? 'Improved'
                    : recommendationData?.improvement < 0
                      ? 'Dipped'
                      : 'NoChange';
            return (
                <Copy
                    value={st(
                        `Recommends/${slug}`,
                        data[0].name,
                        StringHelpers.formatMetric('P', recommendationData?.improvement, user.currencySymbol)
                    )}
                />
            );
        }

        return ArrayHelpers.existsAndHasLength(recommendationData?.improvements) ? (
            <MetricRecommendations
                text={st('Recommends', recommendationData?.best, recommendationData?.worst)}
                metrics={recommendationData?.improvements}
            />
        ) : (
            <span>{SystemText('Dashboard/Widgets/Recommends/NoData')}</span>
        );
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple={false}
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                help={st('Fields/Group/Help')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <CompetencyPicker
                label={st('Fields/Competency/Label')}
                mandatory
                value={values.competency1}
                error={errors.competency1}
                onChange={value => updateProperty(value, 'competency1')}
            />
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default CompetencyOverTime;
